import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  alpha,
  Avatar,
  Badge,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

import { formatUSPhone } from "../../../../utils";
import { BlockedNumberChip } from "../../blocked-numbers/BlockedNumberChip";
import { useMarkAsRead } from "../../sms/contacts/hooks/useMarkAsRead";
import { SmsContact } from "../../sms/contacts/types/SmsContact";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";
import { BulkResolvedMap } from "../hooks/useBulkResolveContacts";

const formatConversationDate = (date?: string | null) => {
  if (!date) return "";

  const value = new Date(date);
  const now = new Date();

  const isToday = value.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = value.toDateString() === yesterday.toDateString();

  if (isToday) return value.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isYesterday) return "Yesterday";

  return value.toLocaleDateString([], { month: "short", day: "numeric" });
};

const getLastMessagePreview = (contact: SmsContact) => {
  if (!contact?.lastMessage) return "";
  if (contact.direction === "OUTBOUND") return `You: ${contact.lastMessage}`;
  return contact.lastMessage;
};

const formatContactType = (type?: string) => {
  if (!type) return "";
  return type
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const roleLabels: Record<string, string> = {
  CLIENT: "Client",
  WORKFORCE: "Workforce",
  VENDOR: "Vendor",
  ADMIN: "Admin",
  client: "Client",
  workforce: "Workforce",
  vendor: "Vendor",
  admin: "Admin",
};

interface SmsContactListProps {
  contacts: SmsContact[];
  isLoading?: boolean;
  isContactSelected: (contact: SmsContact) => boolean;
  handleSelectContact: (contact: SmsContact | null) => void;
  sessionNumber?: string;
  callerInfoMap?: Record<string, any>;
  bulkResolvedMap?: BulkResolvedMap;
  onQuickSave: (phoneNumber: string, anchorEl: HTMLElement) => void;
  onEditContact?: (contact: SmsContact) => void;
}

export function SmsContactList({
  contacts,
  isLoading = false,
  isContactSelected,
  handleSelectContact,
  sessionNumber,
  bulkResolvedMap = {},
  onQuickSave,
  onEditContact,
}: SmsContactListProps) {
  const { theme } = useContactCenterTheme();
  const { mutate: markContactAsRead } = useMarkAsRead(sessionNumber);

  const handleContactClick = (contact: SmsContact) => {
    handleSelectContact(contact);
    markContactAsRead(contact.phoneNumber);
  };

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "auto", px: 1 }}>
      <List disablePadding>
        {contacts.map((contact) => {
          const isSelected = isContactSelected(contact);
          const resolved = contact.contact || bulkResolvedMap?.[contact.phoneNumber];
          const isUnknown = !resolved;

          let primaryLabel = formatUSPhone(contact.phoneNumber);
          let phoneLabel: string | null = null;
          let labelText = "";

          if (resolved) {
            const firstName = (resolved as any).firstName || (resolved as any).name || "";
            const lastName = resolved.lastName || "";
            const nameFromFirstLast = [firstName, lastName].filter(Boolean).join(" ");

            primaryLabel = nameFromFirstLast || formatUSPhone(contact.phoneNumber);
            phoneLabel = formatUSPhone(contact.phoneNumber);

            const roleOrType = (resolved as any).role || resolved.type || "";
            labelText = roleLabels[roleOrType] || formatContactType(roleOrType);
          }

          const lastMessagePreview = getLastMessagePreview(contact);
          const lastMessageDate = formatConversationDate(contact.lastRecordAt);
          const unreadCount = contact.unreadCount ?? 0;
          const hasUnread = unreadCount > 0;

          return (
            <ListItemButton
              key={contact.phoneNumber}
              onClick={() => handleContactClick(contact)}
              sx={{
                py: 1.15,
                px: 1.5,
                mb: 0.6,
                borderRadius: "14px",
                backgroundColor: isSelected ? alpha(theme.shell.accent, 0.12) : "transparent",
                transition: "all 0.2s",
                alignItems: "flex-start",
                "&:hover": {
                  bgcolor: isSelected ? alpha(theme.shell.accent, 0.12) : alpha(theme.shell.accent, 0.05),
                },
              }}
            >
              <ListItemAvatar sx={{ minWidth: 45, pt: 0.2 }}>
                <Badge color="warning" variant="dot" invisible={!hasUnread} overlap="circular">
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: isSelected ? theme.shell.accent : alpha(theme.shell.accent, 0.14),
                      color: isSelected ? "#fff" : theme.shell.accent,
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                </Badge>
              </ListItemAvatar>

              <ListItemText
                sx={{ my: 0, minWidth: 0 }}
                primary={
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Typography component="span" noWrap sx={{ fontSize: "14px", fontWeight: hasUnread ? 800 : 600, color: theme.shell.textPrimary }}>
                      {primaryLabel}
                    </Typography>
                    <BlockedNumberChip phoneNumber={contact.phoneNumber} />
                    {isUnknown && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onQuickSave(contact.phoneNumber, e.currentTarget);
                        }}
                        onMouseEnter={(e) => e.stopPropagation()}
                        onMouseOver={(e) => e.stopPropagation()}
                        sx={{
                          p: 0.25,
                          color: "text.secondary",
                          "&:hover": {
                            color: theme.shell.accent,
                            bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                          },
                        }}
                      >
                        <PersonAddIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    )}
                    {labelText ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                          component="span"
                          sx={{
                            px: 0.8,
                            py: 0.25,
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: 700,
                            bgcolor: isSelected ? alpha(theme.shell.accent, 0.22) : "action.selected",
                            color: isSelected ? theme.shell.accent : "text.secondary",
                            textTransform: "uppercase",
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {labelText}
                        </Box>
                        {!isUnknown && contact.contact && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditContact?.(contact);
                            }}
                            onMouseEnter={(e) => e.stopPropagation()}
                            onMouseOver={(e) => e.stopPropagation()}
                            sx={{
                              p: 0.25,
                              color: "text.secondary",
                              "&:hover": {
                                color: theme.shell.accent,
                                bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        )}
                      </Box>
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    {lastMessageDate ? (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "11px",
                          color: hasUnread ? theme.shell.accent : "text.secondary",
                          whiteSpace: "nowrap",
                          fontWeight: hasUnread ? 700 : 400,
                        }}
                      >
                        {lastMessageDate}
                      </Typography>
                    ) : null}
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ minWidth: 0, display: "block" }}>
                    {phoneLabel ? (
                      <Typography component="span" noWrap sx={{ display: "block", fontSize: "12px", color: "text.secondary", lineHeight: 1.35 }}>
                        {phoneLabel}
                      </Typography>
                    ) : null}
                    {lastMessagePreview ? (
                      <Typography
                        component="span"
                        noWrap
                        sx={{
                          display: "block",
                          fontSize: "12px",
                          color: hasUnread ? "text.primary" : "text.secondary",
                          fontWeight: hasUnread ? 600 : 400,
                          lineHeight: 1.35,
                          mt: phoneLabel ? 0.2 : 0,
                        }}
                      >
                        {lastMessagePreview}
                      </Typography>
                    ) : null}
                  </Box>
                }
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, alignSelf: "center", ml: 1, mt: 0.5 }}>
                {hasUnread ? (
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.7,
                      borderRadius: "999px",
                      bgcolor: theme.shell.accent,
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </Box>
                ) : null}
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
